import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatDateRange } from "@/core/utils/date";
import { IcmPeriodPicker as PayBreakdownPeriodPicker } from "@/icm/common";
import { ApprovalCoverageControl } from "@/payroll/approvals/approval-coverage-control";
import type {
  ApprovalChangeHandler,
  PendingApproval,
} from "@/payroll/approvals/types";
import type { PayPeriodLockTarget } from "@/payroll/pay-periods/pay-period-lock";
import { PayPeriodLockedTitleMark } from "@/payroll/pay-periods/pay-period-locked";
import { RelativeTime } from "@/payroll/pay-periods/relative-time";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { PayBreakdownHeaderActions } from "./pay-breakdown-header-actions";
import { getPayBreakdownPath } from "./routes";
import {
  PAY_BREAKDOWN_CALCULATION_STATE,
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
} from "./types";
import type {
  PayBreakdownCalculationState,
  PayBreakdownCommentsAction,
  PayBreakdownDownloadAction,
  PayBreakdownManualEditsAction,
  PayBreakdownOutput,
  PayBreakdownPeriod,
  PayBreakdownRerunAction,
} from "./types";

interface PayBreakdownHeaderProps {
  selectedPeriod: PayBreakdownPeriod | undefined;
  latestRunAt: string | null | undefined;
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  // Omitted for read-only surfaces: the coverage readout still renders, its
  // marks just aren't settable.
  approvalActions?: {
    pending: PendingApproval | null;
    onChange: ApprovalChangeHandler;
  };
  calculationState: PayBreakdownCalculationState;
  output: PayBreakdownOutput;
  // The XLSX export, offered from the More actions menu.
  aiWorkbookDownload: PayBreakdownDownloadAction;
  onOpenExplanationChat: () => void;
  // The section sub-tab bar, rendered snug BELOW the period header.
  tabs?: ReactNode;
  sectionActions?: ReactNode;
  // Offered in the trailing overflow menu, and only to a customer with
  // whole-payroll authority on a period that is not already locked.
  lock?: PayPeriodLockTarget;
  hideDefaultActions?: boolean;
  showExplanationChatInMenu?: boolean;
  // Entry point for the manual-edits sidebar. It rides on the header while a
  // rerun has left edits awaiting review — the count is what makes it worth the
  // space — and otherwise folds into the More actions menu.
  manualEdits?: PayBreakdownManualEditsAction;
  // Entry point for the comments sidebar, placed the same way.
  comments?: PayBreakdownCommentsAction;
  rerunAction: PayBreakdownRerunAction;
  // Asks every approver on this workbook to sign off. Renders nothing for
  // businesses that have not configured sheet approvals.
  approvalRequest?: ReactNode;
  periodPicker: {
    periods: PayBreakdownPeriod[];
    calendarPeriods: PayBreakdownPeriod[];
    selectedPeriodStart: string | undefined;
    isLoading: boolean;
  };
}

export const PayBreakdownHeader = ({
  selectedPeriod,
  latestRunAt,
  approvalStatus,
  approvalActions,
  calculationState,
  output,
  aiWorkbookDownload,
  onOpenExplanationChat,
  tabs,
  sectionActions,
  lock,
  hideDefaultActions = false,
  showExplanationChatInMenu = false,
  manualEdits,
  comments,
  rerunAction,
  approvalRequest,
  periodPicker,
}: PayBreakdownHeaderProps) => {
  const isLocked = selectedPeriod?.is_locked ?? false;
  const navigate = useNavigate();

  // Reactive readout of the picker's selection — a prominent period-context
  // title, NOT the selector itself (the picker stays a separate control at
  // right). Re-renders whenever the selected period changes.
  const periodTitle = selectedPeriod
    ? formatDateRange(selectedPeriod.period_start, selectedPeriod.period_end)
    : "Payroll";
  const hasApprovalUnits = (approvalStatus?.access_keys.length ?? 0) > 0;
  const isCalculating =
    calculationState !== PAY_BREAKDOWN_CALCULATION_STATE.idle;
  const isRerunning =
    calculationState === PAY_BREAKDOWN_CALCULATION_STATE.rerun;
  const relativePrefix = isCalculating ? "Started calculating" : "Calculated";
  const runRecencyAt =
    calculationState === PAY_BREAKDOWN_CALCULATION_STATE.background
      ? null
      : latestRunAt;

  // The workbook actions — everything that acts on a generated Grid. A section
  // with its own actions, such as PayPacks or PushPay, replaces them wholesale.
  const hasAiWorkbook = output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript;
  const showWorkbookActions = !hideDefaultActions && hasAiWorkbook;
  return (
    <Stack fullWidth gap="sm">
      <Stack
        direction="row"
        align="center"
        justify="between"
        fullWidth
        gap="sm"
        className="flex-wrap gap-y-2"
      >
        <Stack gap="none" className="min-w-0">
          <Stack direction="row" align="center" gap="sm">
            <Typography variant="titleW3" className="whitespace-nowrap">
              {periodTitle}
            </Typography>
            {isLocked && <PayPeriodLockedTitleMark />}
          </Stack>
          {/* Run recency and sign-off coverage are both "state of this period"
              — one line of quiet context under the title, not stacked boxes. */}
          <Stack direction="row" align="center" gap="sm" className="flex-wrap">
            {runRecencyAt && (
              <Stack direction="row" align="center" gap="xs">
                {isRerunning && (
                  <LoadingSpinner
                    role="status"
                    aria-label="Payroll calculation in progress"
                    size="sm"
                    color="secondary"
                    containerClassName="h-auto w-auto"
                  />
                )}
                <RelativeTime
                  prefix={relativePrefix}
                  timestamp={runRecencyAt}
                />
              </Stack>
            )}
            {runRecencyAt && hasApprovalUnits && (
              <Separator orientation="vertical" className="h-3" />
            )}
            <ApprovalCoverageControl
              approvalStatus={approvalStatus}
              pendingApproval={approvalActions?.pending}
              onApprovalChange={approvalActions?.onChange}
            />
          </Stack>
        </Stack>

        {/* Period context first, then the actions that operate on it, with
            everything occasional folded into the trailing overflow menu. */}
        <Stack
          direction="row"
          align="center"
          justify="end"
          gap="sm"
          className="w-full flex-wrap gap-y-2 lg:w-auto"
        >
          <PayBreakdownPeriodPicker
            periods={periodPicker.periods}
            calendarPeriods={periodPicker.calendarPeriods}
            selectedPeriodStart={periodPicker.selectedPeriodStart}
            onSelect={(nextPeriodStart) =>
              navigate(getPayBreakdownPath(nextPeriodStart))
            }
            isLoading={periodPicker.isLoading}
            className="w-full sm:w-72"
          />
          {sectionActions}
          <PayBreakdownHeaderActions
            explanationChat={
              hasAiWorkbook &&
              (showWorkbookActions || showExplanationChatInMenu)
                ? {
                    onOpen: onOpenExplanationChat,
                    placement: showExplanationChatInMenu ? "menu" : "button",
                  }
                : undefined
            }
            workbook={
              showWorkbookActions
                ? {
                    download: aiWorkbookDownload,
                    manualEdits,
                    comments,
                    approvalRequest,
                  }
                : undefined
            }
            rerun={
              hideDefaultActions
                ? undefined
                : { period: selectedPeriod, action: rerunAction }
            }
            lock={lock}
            isLocked={isLocked}
          />
        </Stack>
      </Stack>

      {tabs}
    </Stack>
  );
};
